<script setup lang="ts">
import type {CreditUsage} from '@/client/mentor/CreditUsageClient'
import {DATE_FMT, useDateTimeFormatter} from '@/composable/useDateTimeFormatter'
import {DripInterval} from '@/client/mentor/CreditUsageClient'
import {REQUEST_CREDITS_URL} from '@/components/assistant/constants'
import {toRef} from 'vue'
import {useAssistantTierName} from '@/components/assistant/composables/useAssistantTierName'
import {useLanguageMapping} from '@/composables/useLanguageMapping'

const props = defineProps<{
  creditUsage?: CreditUsage
}>()

const [dateFormatter] = useDateTimeFormatter(DATE_FMT)
const requestCreditsUrl = useLanguageMapping(REQUEST_CREDITS_URL)

const tierName = useAssistantTierName(toRef(() => props.creditUsage?.tier))
</script>

<template>
  <div>
    <span v-if="creditUsage?.isActiveTier" class="font-bold">{{ $t('assistant.chat.notEnoughCredits.title') }}</span>
    <span v-else class="font-bold">{{ $t('assistant.chat.notEnoughCredits.inactiveTierTitle') }}</span>

    <p class="space-x-1">
      <!-- Active tier message -->
      <template v-if="creditUsage?.isActiveTier">
        <span>{{ $t('assistant.chat.notEnoughCredits.youveUsedAllYourCredits') }}</span>
        <span
          v-if="creditUsage?.dripInterval === DripInterval.DAILY"
          v-html="$t('assistant.chat.notEnoughCredits.moreCreditsDaily')"
        ></span>
        <span
          v-else-if="creditUsage?.nextCreditReleaseDate"
          v-html="
            $t('assistant.chat.notEnoughCredits.moreCredits', {date: dateFormatter.format(creditUsage?.nextCreditReleaseDate)})
          "
        ></span>
        <span>{{ $t('assistant.chat.notEnoughCredits.needMoreAiCredits') }}</span>
        <a :href="requestCreditsUrl" rel="noopener noreferrer" target="_blank">
          {{ $t('assistant.chat.notEnoughCredits.requestItNow') }}
        </a>
      </template>

      <!-- Inactive tier message -->
      <template v-else>
        <i18n-t keypath="assistant.chat.notEnoughCredits.inactiveTier" tag="span">
          <template #tier>
            {{ tierName }}
          </template>
          <template #sendUsARequest>
            <a :href="requestCreditsUrl" rel="noopener noreferrer" target="_blank">
              {{ $t('assistant.chat.notEnoughCredits.sendUsARequest') }}
            </a>
          </template>
        </i18n-t>
      </template>
    </p>
  </div>
</template>
